import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

// `vq=hd1080` is YouTube's hint param for "open the player at max quality".
// `hd=1` is the legacy equivalent kept for older clients. We also disable
// related-video clutter and YouTube branding for a cleaner cinema feel.
const ytEmbedUrl = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}` +
  `?autoplay=1&rel=0&modestbranding=1&playsinline=1&vq=hd1080&hd=1`;

/**
 * Fullscreen modal that embeds a YouTube video with audio + controls.
 *
 * Closes only via the explicit ESCI button or the Escape key — clicking
 * the backdrop intentionally does NOT close the modal so that mobile
 * visitors don't dismiss the video by an extra tap on the side.
 */
export default function VideoModal({ video, onClose }) {
  const { t } = useLocale();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-8"
      style={{ background: "rgba(0,0,0,0.94)", backdropFilter: "blur(10px)" }}
      data-testid="cinema-modal"
    >
      {/* Explicit ESCI button — the only way (besides Esc key) to close.
          Sized + spaced generously so mobile thumbs hit it reliably. */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 transition-colors"
        style={{
          color: "var(--text-primary)",
          background: "rgba(0,0,0,0.6)",
          border: "1px solid var(--border-red)",
          backdropFilter: "blur(8px)",
          letterSpacing: "0.3em",
          fontSize: "11px",
          textTransform: "uppercase",
          fontWeight: 400,
          zIndex: 10,
        }}
        data-testid="cinema-modal-close"
      >
        <X size={14} strokeWidth={1.6} />
        {t("cinema.exit")}
      </button>

      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-5xl"
        style={{ aspectRatio: "16 / 9" }}
      >
        <iframe
          data-cookieconsent="ignore"
          title={video.title || "Cinema"}
          src={ytEmbedUrl(video.youtube_id)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          style={{ border: 0, background: "#000" }}
        />
      </motion.div>
    </motion.div>
  );
}
